/**
 * AddVendorPriceDialog — inline form for tile 4 "Missing vendor price".
 *
 * Lets the buyer pick an existing vendor (autocomplete against
 * /api/vendors) or create one inline. Submits to
 * POST /procurement-dashboard/missing-vendor-price/add which writes
 * both `vendor_rm_prices` and a `price_history` seed.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AddVendorPriceDialog({ rm, open, onClose, onSuccess }) {
  const [vendorQuery, setVendorQuery] = useState("");
  const [vendorResults, setVendorResults] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showNewVendor, setShowNewVendor] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: "", gst: "", phone: "", email: "" });
  const [price, setPrice] = useState("");
  const [currency] = useState("INR");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  // Debounced vendor search. The `cancelled` flag guarantees we don't
  // setState after the input has changed again, which keeps the
  // react-hooks/set-state-in-effect rule satisfied (no synchronous
  // state writes during effect setup — only after the async await).
  useEffect(() => {
    if (showNewVendor || !vendorQuery || vendorQuery.length < 2) {
      // Schedule the clear via a microtask so it doesn't fire during
      // the effect's synchronous body.
      const id = setTimeout(() => setVendorResults([]), 0);
      return () => clearTimeout(id);
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/vendors?search=${encodeURIComponent(vendorQuery)}&limit=10`);
        if (cancelled) return;
        const items = res.data?.items || res.data?.vendors || (Array.isArray(res.data) ? res.data : []);
        setVendorResults(items.slice(0, 10));
      } catch {
        if (!cancelled) setVendorResults([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [vendorQuery, showNewVendor]);

  const submit = async () => {
    const priceNum = parseFloat(price);
    if (!priceNum || priceNum <= 0) {
      toast.error("Enter a price > 0");
      return;
    }
    if (!selectedVendor && !showNewVendor) {
      toast.error("Select an existing vendor or create a new one");
      return;
    }
    if (showNewVendor && !newVendor.name.trim()) {
      toast.error("New vendor name is required");
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        rm_id: rm.rm_id,
        price: priceNum,
        currency,
        effective_date: effectiveDate || null,
      };
      if (showNewVendor) {
        body.new_vendor = newVendor;
      } else {
        body.vendor_id = selectedVendor.vendor_id;
      }
      await axios.post(`${API}/procurement-dashboard/missing-vendor-price/add`, body);
      onSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to add vendor price";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-vendor-price-dialog">
        <DialogHeader>
          <DialogTitle>Add vendor &amp; price</DialogTitle>
          <DialogDescription className="font-mono text-xs">
            {rm.rm_id} · {rm.name || "(no description)"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Vendor picker */}
          {!showNewVendor ? (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Vendor</Label>
              {selectedVendor ? (
                <div className="flex items-center justify-between border rounded-sm px-3 py-2 bg-zinc-50" data-testid="selected-vendor">
                  <span className="text-sm font-mono">
                    <span className="font-bold">{selectedVendor.vendor_id}</span> · {selectedVendor.name}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedVendor(null)}>×</Button>
                </div>
              ) : (
                <>
                  <Input
                    placeholder="Search vendor name…"
                    value={vendorQuery}
                    onChange={(e) => setVendorQuery(e.target.value)}
                    data-testid="vendor-search-input"
                  />
                  {vendorResults.length > 0 && (
                    <div className="border rounded-sm divide-y max-h-48 overflow-y-auto">
                      {vendorResults.map((v) => (
                        <button
                          key={v.vendor_id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-zinc-50 text-sm font-mono"
                          onClick={() => { setSelectedVendor(v); setVendorQuery(""); setVendorResults([]); }}
                          data-testid={`vendor-result-${v.vendor_id}`}
                        >
                          <span className="font-bold">{v.vendor_id}</span> · {v.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewVendor(true)}
                    data-testid="add-new-vendor-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" /> New vendor
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2 border rounded-sm p-3 bg-amber-50/40">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold uppercase tracking-wider">New vendor</Label>
                <Button variant="ghost" size="sm" onClick={() => setShowNewVendor(false)}>× Cancel</Button>
              </div>
              <Input
                placeholder="Vendor name *"
                value={newVendor.name}
                onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                data-testid="new-vendor-name"
              />
              <Input
                placeholder="GST (optional)"
                value={newVendor.gst}
                onChange={(e) => setNewVendor({ ...newVendor, gst: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Phone"
                  value={newVendor.phone}
                  onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                />
                <Input
                  placeholder="Email"
                  value={newVendor.email}
                  onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                />
              </div>
            </div>
          )}

          {/* Price + effective date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Price (₹) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
                data-testid="price-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Effective date</Label>
              <Input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                data-testid="effective-date-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="submit-add-price-btn">
            {submitting ? "Saving…" : "Save price"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
